// Shared chrome for Soulstack Studio screens, ported from the design source
// (ui-shared.jsx). Exports: Glyph, Logo, Arrow, Check, CenteredLoading,
// MarketingNav, MarketingFooter, CtaBand, SignupCta, PlayCta, PlayLinkWrap,
// InDevelopmentBanner.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from '../design/router';
import { useAuth } from '../auth/AuthProvider';
import logoUrl from '../design/assets/soulstack-logo.png';
import { ThemeToggle } from '../design/theme';
import { PLAY_OPEN, SIGNUP_OPEN } from '../config/studioStatus';

/**
 * Centered "Loading…" text sized to fill its own container, for a loading
 * state nested inside an already-chromed page (a project tab, a public wiki
 * page) - as opposed to studio.css's `.studio-loading`, which is 100vh tall
 * and meant for a top-level gate that owns the whole viewport (auth check,
 * initial app boot). Several call sites had each hand-rolled this same
 * flex-center-a-muted-span treatment independently; this consolidates them.
 */
export function CenteredLoading({ text = 'Loading…', minHeight = '60vh' }: { text?: string; minHeight?: number | string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight }}>
      <span style={{ color: 'var(--ss-text-3)', fontSize: 13.5 }}>{text}</span>
    </div>
  );
}

/**
 * Primary "start building" call to action. While sign-ups are closed it reads
 * "Coming soon" and lands on the coming-soon screen, so no button ever promises
 * an action the visitor cannot take. Reopens to its real label in one place
 * (see config/studioStatus) when SIGNUP_OPEN flips true.
 */
export function SignupCta({
  label = 'Start building',
  className = 'ss-btn ss-btn--primary ss-btn--sm',
}: {
  label?: string;
  className?: string;
}) {
  return (
    <Link className={className} to="/signup">
      {SIGNUP_OPEN ? label : 'Coming soon'}
    </Link>
  );
}

/**
 * A "play this game" button (Play Sunder, Play now, etc). While PLAY_OPEN is
 * false (see config/studioStatus) it stays in place with its real href/to
 * intact but renders as an inert "Coming soon" pill - clicking it does
 * nothing - so hosted games aren't reachable from the site before they're
 * ready. Flip PLAY_OPEN to restore the real link with no other changes.
 */
export function PlayCta({
  href,
  to,
  className = 'ss-btn ss-btn--bone',
  children,
}: {
  href?: string;
  to?: string;
  className?: string;
  children: ReactNode;
}) {
  if (!PLAY_OPEN) {
    return (
      <span className={className + ' ss-btn--disabled'} aria-disabled="true">
        Coming soon
      </span>
    );
  }
  return href ? (
    <a className={className} href={href}>{children}</a>
  ) : (
    <Link className={className} to={to!}>{children}</Link>
  );
}

/**
 * Same gate as PlayCta but for non-button "play" links (a cover image or
 * title acting as the click target) - becomes an inert element with the
 * same layout instead of a pill, since these usually sit inside a card.
 */
export function PlayLinkWrap({
  href,
  to,
  className,
  style,
  children,
}: {
  href?: string;
  to?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (!PLAY_OPEN) {
    return (
      <div className={className} style={{ ...style, cursor: 'default' }} aria-disabled="true">
        {children}
      </div>
    );
  }
  return href ? (
    <a className={className} href={href} style={style}>{children}</a>
  ) : (
    <Link className={className} to={to!} style={style}>{children}</Link>
  );
}

/**
 * Slim ribbon shown directly beneath the marketing nav while Studio is in
 * development. Renders nothing once sign-ups are open.
 */
export function InDevelopmentBanner() {
  if (SIGNUP_OPEN) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '7px 16px',
        fontSize: 12.5,
        fontWeight: 500,
        textAlign: 'center',
        color: 'var(--ss-text-2)',
        background: 'var(--ss-accent-soft)',
        borderBottom: '1px solid var(--ss-accent-border)',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--ss-accent)',
          flexShrink: 0,
        }}
      />
      <span>
        Soulstack Studio is in active development - public sign-ups aren&rsquo;t open yet.
      </span>
    </div>
  );
}

export function Glyph({ size = 26 }: { size?: number }) {
  return <img className="ss-logo-img" src={logoUrl} alt="Soulstack" style={{ height: size }} />;
}

export function Logo({ size = 30, to = '/' }: { size?: number; to?: string }) {
  return (
    <Link className="ss-logo" to={to}>
      <Glyph size={size} />
      <span className="ss-logo-word">
        Soulstack <span>Studio</span>
      </span>
    </Link>
  );
}

export function Arrow() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path
        d="M2 6.5h9M7.2 2.7l3.8 3.8-3.8 3.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Check() {
  return (
    <svg className="ss-check" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M2.5 7.5l3 3 6-7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Magnifying-glass icon for search fields (`.ss-search`). */
export function SearchIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M7 12.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11zM14.5 14.5L11 11"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Hamburger/close glyph for a mobile nav toggle. */
export function BurgerIcon({ open, size = 20 }: { open: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      {open ? (
        <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      ) : (
        <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  );
}

export function MarketingNav({ active }: { active?: string }) {
  const { user } = useAuth();
  const links = [
    { label: 'Features', to: '/features' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Showcase', to: '/showcase' },
    { label: 'Docs', to: '/docs' },
    { label: 'Wiki', to: '/wikis' },
  ];
  const [open, setOpen] = useState(false);

  // Below the mk-nav-burger breakpoint, close the panel on route change (a
  // link click unmounts nothing since routing is client-side) and on Escape;
  // lock body scroll while it's open so the page behind doesn't scroll too.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <nav className="mk-nav">
        <Logo />
        <div className="mk-nav-links">
          {links.map((l) => (
            <Link
              key={l.label}
              className={'mk-nav-link' + (l.label === active ? ' is-active' : '')}
              to={l.to}
              aria-current={l.label === active ? 'page' : undefined}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="mk-nav-cta">
          <ThemeToggle />
          {user ? (
            <Link className="ss-btn ss-btn--primary ss-btn--sm" to="/app">
              Open studio
            </Link>
          ) : (
            <>
              <Link className="ss-btn ss-btn--ghost ss-btn--sm" to="/signin">
                Sign in
              </Link>
              <SignupCta label="Start building" />
            </>
          )}
        </div>
        <button
          type="button"
          className="mk-nav-burger"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <BurgerIcon open={open} />
        </button>
      </nav>
      {open && (
        <div className="mk-nav-mobile" role="dialog" aria-modal="true">
          <div className="mk-nav-mobile-links">
            {links.map((l) => (
              <Link
                key={l.label}
                className={'mk-nav-mobile-link' + (l.label === active ? ' is-active' : '')}
                to={l.to}
                aria-current={l.label === active ? 'page' : undefined}
                onClick={() => setOpen(false)}
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="mk-nav-mobile-cta">
            {user ? (
              <Link className="ss-btn ss-btn--primary" to="/app" onClick={() => setOpen(false)}>
                Open studio
              </Link>
            ) : (
              <>
                <Link className="ss-btn ss-btn--ghost" to="/signin" onClick={() => setOpen(false)}>
                  Sign in
                </Link>
                <SignupCta label="Start building" className="ss-btn ss-btn--primary" />
              </>
            )}
            <div className="mk-nav-mobile-theme">
              <span>Theme</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      )}
      <InDevelopmentBanner />
    </>
  );
}

export function CtaBand() {
  return (
    <section
      style={{
        padding: '120px clamp(20px, 6vw, 56px)',
        textAlign: 'center',
        borderTop: '1px solid var(--ss-border-soft)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse 55% 70% at 50% 100%, var(--ss-glow), transparent 70%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          maxWidth: 640,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 28,
        }}
      >
        <Glyph size={44} />
        <h2 className="ss-display" style={{ fontSize: 'clamp(2rem, 6vw, 3rem)' }}>
          Your first game is a browser tab away.
        </h2>
        <p style={{ color: 'var(--ss-text-2)', fontSize: 17 }}>
          {SIGNUP_OPEN ? 'Free to start. Nothing to install to build it, and a real app at the end.' : 'In active development. Nothing to install to build it, and a real app at the end.'}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <SignupCta label="Start building free" className="ss-btn ss-btn--primary ss-btn--lg" />
          <Link className="ss-btn ss-btn--ghost ss-btn--lg" to="/features">
            See the studio
          </Link>
        </div>
      </div>
    </section>
  );
}

export function MarketingFooter() {
  const cols = [
    {
      title: 'Product',
      links: [
        { label: 'Scene Editor', to: '/features/editor' },
        { label: 'Art & Assets', to: '/features/art' },
        { label: 'Characters', to: '/features/characters' },
        { label: 'Logic & Systems', to: '/features/logic' },
        { label: 'Multiplayer', to: '/features/multiplayer' },
        { label: 'Hosting & Publishing', to: '/features/hosting' },
        { label: 'Pricing', to: '/pricing' },
        { label: 'Changelog', to: '/changelog' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Docs', to: '/docs' },
        { label: 'Quickstart', to: '/docs/quickstart' },
        { label: 'Keyboard shortcuts', to: '/docs/reference/shortcuts' },
        { label: 'Engine API', to: '/docs/reference' },
        { label: 'Wiki', to: '/wikis' },
        { label: 'Showcase', to: '/showcase' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About', to: '/about' },
        { label: 'Blog', to: '/blog' },
        { label: 'Contact', to: '/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms', to: '/legal/terms' },
        { label: 'Privacy', to: '/legal/privacy' },
        { label: 'Acceptable use', to: '/legal/aup' },
        { label: 'Attributions', to: '/attributions' },
      ],
    },
  ];
  return (
    <footer className="mk-footer">
      <div className="mk-footer-grid">
        <div className="mk-footer-brand">
          <Logo />
          <p>Build games in your browser. Host them, install them, play them anywhere.</p>
        </div>
        {cols.map((c) => (
          <div key={c.title} className="mk-footer-col">
            <span className="mk-footer-col-title">{c.title}</span>
            {c.links.map((l) => (
              <Link key={l.label} to={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <div className="mk-footer-base">
        <span>© 2026 Soulstack Ltd</span>
        <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, letterSpacing: '0.14em' }}>
          SSTK.STUDIO
        </span>
      </div>
    </footer>
  );
}

export interface NotFoundCta {
  label: string;
  to: string;
  variant?: 'primary' | 'ghost';
}

/**
 * Shared "nothing here" content block: Glyph, an ss-display heading, a body
 * paragraph and a CTA row. Deliberately doesn't own page chrome (marketing
 * nav/footer, a play-specific top bar, or none at all) since call sites
 * genuinely differ on that - it just renders the centred recipe every
 * not-found screen already agreed on, and each call site wraps it in
 * whatever chrome (or lack of it) that screen needs.
 */
export function NotFoundScreen({
  className,
  style,
  glyphSize = 40,
  title,
  titleStyle,
  body,
  bodyStyle,
  ctas,
  ctaMarginTop = 8,
}: {
  className?: string;
  style?: CSSProperties;
  glyphSize?: number;
  title: ReactNode;
  titleStyle?: CSSProperties;
  body: ReactNode;
  bodyStyle?: CSSProperties;
  ctas: NotFoundCta[];
  ctaMarginTop?: number;
}) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        textAlign: 'center',
        padding: '0 40px',
        ...style,
      }}
    >
      <Glyph size={glyphSize} />
      <h1 className="ss-display" style={{ fontSize: 'clamp(1.7rem, 7vw, 2.5rem)', ...titleStyle }}>
        {title}
      </h1>
      <p style={{ fontSize: 14.5, color: 'var(--ss-text-3)', maxWidth: 420, lineHeight: 1.6, ...bodyStyle }}>
        {body}
      </p>
      <div style={{ display: 'flex', gap: 12, marginTop: ctaMarginTop }}>
        {ctas.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className={'ss-btn ' + (c.variant === 'ghost' ? 'ss-btn--ghost' : 'ss-btn--primary')}
          >
            {c.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
